import './ModalVerificationCodeConfig';
import { alpha, useTheme } from '@mui/material';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useVerificationCodeCache } from '../../../cache/learner/verificationCodeReactiveVar';
import { MFACodeSentEnum } from '../../../constants/enums/MfaCodeSentEnum';
import { MFAMessageSentEnum } from '../../../constants/enums/MFAMessageSentEnum';
import { TIME_RESEND_CODE } from '../../../constants/modalVerificationCode';
import { ModalContext } from '../../../contexts/ModalContext';
import useMFAAuthenticationHook from '../../../hooks/useMFAAuthenticationHook/useMFAAuthenticationHook';
import { ISetMFAAuthenticationByUserIdResponse } from '../../../operations/mutations/mfa/useSetMFAAuthenticationByUserId/useSetMFAAuthenticationByUserId';
import { COLORS } from '../../../theme/colors';
import { handleConvertNumberUtil } from '../../../utils/handleConvertNumberUtil';
import { handleFormatCompletedUtil } from '../../../utils/handleFormatCompletedUtil';
import MultiCodeInputComponent from '../../../views/Auth/MultiFactorAuthetication/components/CardVerificationCode/components/MultiCodeInputComponent/MultiInputComponent';
import AtomButton from '../../atoms/AtomButton/AtomButton';
import AtomFlex from '../../atoms/AtomFlex';
import AtomGrid from '../../atoms/AtomGrid';
import AtomTypography from '../../atoms/AtomTypography/AtomTypography';
import AuthenticatorBoldIcon from '../../icons/svg/AuthenticatorBoldIcon';
import XIcon from '../../icons/svg/XIcon';
import { styleConfigs } from '../../resources/styleConfigs';
import NewModalLayoutComponent from '../ModalComponent/NewModalLayoutComponent';

interface IVerificationCodeData {
  data?: ISetMFAAuthenticationByUserIdResponse;
  setValidationSuccess: (value?: boolean) => void;
}

export interface IModalVerificationCodeProps {
  directProps?: {
    verificationCodeData: IVerificationCodeData;
  };
}

const ModalVerificationCode: FC<IModalVerificationCodeProps> = (props) => {
  const { directProps } = props;
  const { t } = useTranslation('ModalVerificationCode');
  const theme = useTheme();
  const [activeSuperiorButton, setActiveSuperiorButton] = useState<boolean>(true);
  const [activeCode, setActiveCode] = useState<string>();
  const { isOpen, modalClose } = useContext(ModalContext);
  const { setCreateVerificationCodeId } = useVerificationCodeCache();
  const endTimeRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const { operations } = useMFAAuthenticationHook();
  const [resendMessage, setResendMessage] = useState<string>();

  const [secondsLeft, setSecondsLeft] = useState<number>(
    handleConvertNumberUtil(directProps?.verificationCodeData.data?.setMFAAuthenticationByUserId.nextAttempt),
  );

  const handleResendMFACode = async (): Promise<void> => {
    const resendMFACodeResult = await operations.handleResendMFACode({ sentType: MFACodeSentEnum.ACTIVATION });

    const handleSeconds = handleConvertNumberUtil(
      resendMFACodeResult?.resendMFACodeByUserIdAndSentType.nextAttempt ?? TIME_RESEND_CODE,
    );

    setSecondsLeft(handleSeconds);
    endTimeRef.current = Date.now() + handleSeconds * 1000;

    setResendMessage(resendMFACodeResult?.resendMFACodeByUserIdAndSentType.message);
  };

  useEffect(() => {
    let initialSeconds = handleConvertNumberUtil(
      operations.validateMFACodeData?.validateMFACode.nextAttempt ??
        directProps?.verificationCodeData.data?.setMFAAuthenticationByUserId.nextAttempt,
    );

    if (!initialSeconds) {
      initialSeconds = handleConvertNumberUtil(TIME_RESEND_CODE);
    }

    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const endTime = Date.now() + initialSeconds * 1000;

    endTimeRef.current = endTime;
    setSecondsLeft(initialSeconds);

    const tick = (): void => {
      if (!endTimeRef.current) {
        return;
      }

      const msLeft = endTimeRef.current - Date.now();
      const secs = Math.max(0, Math.ceil(msLeft / 1000));

      setSecondsLeft(secs);

      if (secs <= 0 && intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    tick();
    intervalRef.current = window.setInterval(tick, 1000);

    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      endTimeRef.current = null;
    };
  }, [
    operations.validateMFACodeData?.validateMFACode.nextAttempt,
    directProps?.verificationCodeData.data?.setMFAAuthenticationByUserId.nextAttempt,
    isOpen,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setCreateVerificationCodeId(undefined);
    }
  }, [isOpen]);

  useEffect(() => {
    if (operations.validateMFACodeData?.validateMFACode.success === true && isOpen) {
      modalClose();
    }
    directProps?.verificationCodeData.setValidationSuccess(operations.validateMFACodeData?.validateMFACode.success);
  }, [operations.validateMFACodeData?.validateMFACode.success, isOpen]);

  useEffect(() => {
    setSecondsLeft(handleConvertNumberUtil(operations.validateMFACodeData?.validateMFACode.nextAttempt));
  }, [operations.validateMFACodeData?.validateMFACode.attempts]);

  return (
    <NewModalLayoutComponent activeHeader={false}>
      <AtomFlex
        py={styleConfigs.marginPadding.custom1}
        px={styleConfigs.marginPadding.custom4}
        gap={styleConfigs.marginPadding.custom5}
      >
        <AtomFlex
          maxWidth={styleConfigs.dimensions.width380}
          alignSelf="center"
          alignItems="center"
          gap={styleConfigs.marginPadding.custom2}
        >
          <AuthenticatorBoldIcon />
          <AtomFlex alignItems="center" textAlign="center">
            <AtomTypography variant="h2">{t('MODAL_VERIFICATION_TITLE')}</AtomTypography>
            <AtomTypography variant="subtitle2" fontWeight={400} color={alpha(COLORS.black, 0.6)}>
              {t('MODAL_VERIFICATION_DESCRIPTION')}
            </AtomTypography>
          </AtomFlex>
        </AtomFlex>
        <MultiCodeInputComponent
          setActiveSuperiorButton={setActiveSuperiorButton}
          setActiveCode={setActiveCode}
          validateMFACodeData={operations.validateMFACodeData}
        />
        <AtomFlex gap={styleConfigs.marginPadding.custom1}>
          <AtomButton
            onClick={() =>
              operations.handleValidateMFACode({ code: activeCode ?? '', type: MFACodeSentEnum.ACTIVATION })
            }
            variant="contained"
            disabled={activeSuperiorButton}
            fullWidth
            sx={{
              borderRadius: styleConfigs.borders.radius4rem,
            }}
            data-testid="verify_code_button"
            data-cy-button-type="verify_mfa_code"
            data-cy-disabled={activeSuperiorButton}
          >
            {t('MODAL_VERIFICATION_BUTTON')}
          </AtomButton>
          <AtomGrid
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={styleConfigs.marginPadding.custom2}
          >
            <AtomTypography variant="subtitle2" fontWeight={400} color={alpha(COLORS.black, 0.6)}>
              {resendMessage === MFAMessageSentEnum.FIRST_ATTEMPT ||
              resendMessage === MFAMessageSentEnum.SECOND_ATTEMPT ||
              resendMessage === MFAMessageSentEnum.THIRD_ATTEMPT ||
              operations.validateMFACodeData?.validateMFACode.message === MFAMessageSentEnum.THIRD_ATTEMPT
                ? t('MODAL_VERIFICATION_FAILED_ATTEMPTS')
                : t('MODAL_VERIFICATION_NOT_RECEIVED_CODE')}
            </AtomTypography>
            {secondsLeft > 0 ? (
              <AtomGrid display="flex" alignItems="center" gap={styleConfigs.marginPadding.custom0p625}>
                <AtomTypography variant="subtitle2" fontWeight={400} color={alpha(COLORS.black, 0.6)}>
                  {resendMessage === MFAMessageSentEnum.FIRST_ATTEMPT ||
                  resendMessage === MFAMessageSentEnum.SECOND_ATTEMPT ||
                  resendMessage === MFAMessageSentEnum.THIRD_ATTEMPT ||
                  operations.validateMFACodeData?.validateMFACode.message === MFAMessageSentEnum.THIRD_ATTEMPT
                    ? t('MODAL_VERIFICATION_TRY_AGAIN')
                    : t('MODAL_VERIFICATION_TEXT_TIMER')}
                </AtomTypography>
                <AtomTypography variant="subtitle2" fontWeight={400} color={theme.palette.text.redViolet}>
                  {handleFormatCompletedUtil(secondsLeft)}
                </AtomTypography>
              </AtomGrid>
            ) : (
              <AtomTypography
                variant="subtitle2"
                fontWeight={400}
                color={theme.palette.text.redViolet}
                sx={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={() => handleResendMFACode()}
              >
                {t('MODAL_VERIFICATION_RESEND_CODE')}
              </AtomTypography>
            )}
          </AtomGrid>
        </AtomFlex>
        <AtomFlex
          onClick={modalClose}
          padding={styleConfigs.marginPadding.custom0p5}
          position="absolute"
          top={styleConfigs.marginPadding.custom26}
          right={styleConfigs.marginPadding.custom26}
          sx={{
            ':hover': {
              background: alpha(COLORS.mistBlue, 0.1),
              borderRadius: styleConfigs.borders.borderRound,
            },
          }}
        >
          <XIcon />
        </AtomFlex>
      </AtomFlex>
    </NewModalLayoutComponent>
  );
};

export default ModalVerificationCode;
